import axios from 'axios';
import { orderBy, filterBy, groupBy, GroupDescriptor } from '@progress/kendo-data-query';
import { toasterAlert } from '../redux/toasterSlice';
import { store } from '../store';
import { ROLES } from './Constant';
import { setAttendantUserSummary, setSignOffStats } from '../redux/globalSlice';

const getUserToken = () => {
   return localStorage.getItem('RPMAuthToken') ?? null
}

const logOut = () => {
  localStorage.removeItem('RPMAuthToken')
  window.location.reload();
}

const getAttendantUserSummary = async (cancelToken?: any) => {
  const roles = (store.getState().global.authUserAllData as { roles?: string[] })?.roles;
  if (!roles?.includes(ROLES.ATTENDANT)) {
    return;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}api/dashboard/top-metrics`, {
    cancelToken: cancelToken
  }).then((response: any) => {
    store.dispatch(setAttendantUserSummary(response));
  }).catch((e: any) =>
    store.dispatch(
      toasterAlert({
        type: 'error',
        title: 'Error',
        message: e?.response?.data?.hint || 'Failed to refresh attendant user summary.'
      })
    )
  );
}

export type AddressPostalLookupParams = {
  address?: string;
  city?: string;
  state?: string;
};

export type PostalCodeLookupResult = {
  postalCode: string | null;
  error?: string;
  hint?: string;
};

const getPostalCodeLookupMessage = (result: PostalCodeLookupResult): string => {
  // const message = [result.hint, result.error].filter(Boolean).join(' - ');
  return result.error || "Couldn't lookup postal code";
};

const getPostalCodeFromAddress = async ({
  address,
  city,
  state
}: AddressPostalLookupParams, cancelToken?: any): Promise<PostalCodeLookupResult> => {
  const trimmedAddress = address?.trim();
  const trimmedCity = city?.trim();
  const trimmedState = state?.trim();

  if (!trimmedAddress || !trimmedCity || !trimmedState) {
    return { postalCode: null };
  }

  try {
    const response: { postalCode?: string } = await axios.post(
      `${process.env.REACT_APP_API_URL}api/address/postal-code`,
      {
        address: trimmedAddress,
        city: trimmedCity,
        state: trimmedState.toUpperCase()
      },
      { cancelToken }
    );

    const postalCode = response?.postalCode?.trim() || null;
    if (!postalCode) {
      return {
        postalCode: null,
        error: 'no postal code found for address',
        hint: "couldn't lookup postal code"
      };
    }

    return { postalCode };
  } catch (error: any) {
    return {
      postalCode: null,
      error: error?.response?.data?.error,
      hint: error?.response?.data?.hint
    };
  }
};

export type PostalCodeBulkSyncParams = {
  patientIds: number[];
};

export type PostalCodeBulkSyncResult = {
  status: 'success' | 'failed';
};

const syncPostalCodeBulk = async (
  { patientIds }: PostalCodeBulkSyncParams,
  cancelToken?: any
): Promise<boolean> => {
  const uniquePatientIds = Array.from(
    new Set(patientIds.filter((id) => Number.isInteger(id) && id > 0))
  );

  if (uniquePatientIds.length === 0) {
    return false;
  }

  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}api/address/postal-code-bulk`,
    { patientIds: uniquePatientIds },
    { cancelToken }
  );

  if (response) {
    store.dispatch(toasterAlert({
      type: 'success',
      title: 'Success',
      message: response || 'Zip codes synced successfully'
    }));
  }

  return true;
};

const getUnsignedSignOffStats = async (cancelToken?: any) => {
  return axios.get(`${process.env.REACT_APP_API_URL}api/unsigned-stats`, {
    cancelToken
  }).then((stats: any) => {
    const periods = stats?.periods ?? 0;
    const carePlans = stats?.carePlans ?? 0;
    const rtmPeriods = stats?.rtmPeriods ?? 0;
    const prepareCarePlans = stats?.prepareCarePlans ?? 0;
    store.dispatch(setSignOffStats({
      periods,
      carePlans,
      rtmPeriods,
      prepareCarePlans,
      total: periods + carePlans + rtmPeriods
    }));
  }).catch((e: any) =>
    store.dispatch(
      toasterAlert({
        type: 'error',
        title: 'Error',
        message: e?.response?.data?.hint || 'Failed to refresh unsigned sign-off stats.'
      })
    )
  );
};

const normalizeDateFilters = (inputFilter: any, dateFields: string[]): any => {
  const dateFieldSet = new Set(dateFields);

  const toDateOrNull = (val: any): Date | null => {
    if (val == null || val === '') return null;
    if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  // Use local day boundaries so date-equals covers the selected local day.
  const startOfDayLocal = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const nextDayLocal = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);

  const transform = (f: any): any => {
    if (!f) return f;
    if (f.filters && Array.isArray(f.filters)) {
      return {
        ...f,
        filters: f.filters.map(transform)
      };
    }

    if (f.field && dateFieldSet.has(f.field)) {
      const valueAsDate = toDateOrNull(f.value);
      if (!valueAsDate) {
        return { ...f, value: null };
      }

      if (f.operator === 'eq') {
        const start = startOfDayLocal(valueAsDate);
        const end = nextDayLocal(valueAsDate);
        return {
          logic: 'and',
          filters: [
            { field: f.field, operator: 'gte', value: start },
            { field: f.field, operator: 'lt', value: end }
          ]
        };
      }

      return { ...f, value: valueAsDate };
    }

    return f;
  };

  return transform(inputFilter);
}

/** Billing grids: sort, normalize date filters, optional groupBy, then page. Returns total for Kendo pagination. */
const processWithGroups = (
  data: any[],
  group: GroupDescriptor[],
  filter: any,
  sort: any,
  skip: number,
  take: number,
  dateFields: string[]
): { processedData: any[]; totalRecords: number } => {
  let newData = [...data];

  if (sort && sort.length > 0) {
    newData = orderBy(newData, sort);
  }

  if (filter) {
    const normalized = normalizeDateFilters(filter, dateFields);
    newData = filterBy(newData, normalized);
  }

  if (group && group.length > 0) {
    const groupedData = groupBy(newData, group);
    const totalRecords = groupedData.length;
    const processedData = groupedData.slice(skip, skip + take);
    return { processedData, totalRecords };
  }

  const totalRecords = newData.length;
  const processedData = newData.slice(skip, skip + take);
  return { processedData, totalRecords };
};

/** Kendo grid: sort + filter + page (no grouping). Use `totalRecords` for server-style pagination total. */
const processData = (
  data: any[],
  filter: any,
  sort: any,
  skip: number,
  take: number
): { processedData: any[]; totalRecords: number } => {
  let newData = [...data];

  if (sort && sort.length > 0) {
    newData = orderBy(newData, sort);
  }

  if (filter) {
    newData = filterBy(newData, filter);
  }

  const totalRecords = newData.length;
  const processedData = newData.slice(skip, skip + take);
  return { processedData, totalRecords };
};

const formatPhoneValue = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatPostalCodeInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export {
  getUserToken,
  logOut,
  getAttendantUserSummary,
  getPostalCodeFromAddress,
  getPostalCodeLookupMessage,
  syncPostalCodeBulk,
  getUnsignedSignOffStats,
  normalizeDateFilters,
  processWithGroups,
  processData,
  formatPhoneValue,
  formatPostalCodeInput
}
